// The video slide of the public lightbox (phase 088), shared since full phase
// 093 by the post page's strip (`gallery`) and a post card's video
// (`video-card`, `doc/breeders-pitomniks-base.md` 6.6), so a card opens the
// very slide the post page shows.
//
// PhotoSwipe has no video type of its own: a link of the
// `public/shared/_video_link` contract (`data-pswp-type="video"`) becomes the
// slide data below, and the slide is a box PhotoSwipe sizes to the rendition's
// own dimensions, holding a `<video>` with the browser's own controls.
//
// A slide opened by a press plays at once, with sound, inside the press's user
// activation; a browser that refuses sound (`NotAllowedError`) is asked again
// muted, and the browser's controls turn the sound on. Reduced motion turns
// off only the lightbox's zoom, never the playback the visitor asked for.

// The part of `HTMLVideoElement` the playback rule touches.
export interface PlayableVideo {
  muted: boolean
  play(): Promise<void>
}

// The slide data of a video link: the rendition is `data-pswp-video-src` when
// the link itself leads elsewhere (a card's `<post path>#play`), else the
// link's own address.
export function videoItemData(link: HTMLAnchorElement): Record<string, unknown> {
  const data: Record<string, unknown> = {
    type: "video",
    videoSrc: link.dataset.pswpVideoSrc ?? link.href,
    videoType: link.dataset.pswpVideoType ?? "",
    poster: link.dataset.pswpPoster ?? "",
    alt: link.getAttribute("aria-label") ?? ""
  }
  const width = Number(link.dataset.pswpWidth)
  const height = Number(link.dataset.pswpHeight)
  if (width > 0 && height > 0) Object.assign(data, { width, height })
  return data
}

// The slide: the video filling the box, and under it the link to the post
// when the data names one (`postUrl`, `postLabel`).
export function videoSlide(data: Record<string, unknown>): HTMLDivElement {
  const slide = document.createElement("div")
  slide.className = "pswp__video-slide"
  const video = document.createElement("video")
  video.className = "pswp__video"
  video.controls = true
  video.preload = "none"
  video.playsInline = true
  if (typeof data.poster === "string" && data.poster !== "") video.poster = data.poster
  if (typeof data.alt === "string" && data.alt !== "") video.setAttribute("aria-label", data.alt)

  const source = document.createElement("source")
  source.src = typeof data.videoSrc === "string" ? data.videoSrc : ""
  if (typeof data.videoType === "string" && data.videoType !== "") source.type = data.videoType
  video.appendChild(source)
  slide.appendChild(video)

  if (typeof data.postUrl === "string" && data.postUrl !== "" && typeof data.postLabel === "string") {
    const link = document.createElement("a")
    link.className = "pswp__video-post"
    link.href = data.postUrl
    link.textContent = data.postLabel
    slide.appendChild(link)
  }
  return slide
}

// Plays with sound, and muted when the browser refuses sound. Any other
// refusal (no source the browser can play) leaves the video to its controls.
export async function playWithSound(video: PlayableVideo): Promise<void> {
  video.muted = false
  try {
    await video.play()
  } catch (error) {
    if (!notAllowed(error)) return

    video.muted = true
    await video.play().catch(() => undefined)
  }
}

// The lightbox options of reduced motion: no zoom on opening and closing.
export function lightboxMotion(): { showAnimationType?: "none"; hideAnimationType?: "none" } {
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  return reduced ? { showAnimationType: "none", hideAnimationType: "none" } : {}
}

function notAllowed(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { name?: unknown }).name === "NotAllowedError"
}
